import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

export const ACTIVE_BOARD_STORAGE_KEY = 'sortmyprep.activeBoardUuid';

function readStorage() {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(ACTIVE_BOARD_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(uuid) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (uuid) localStorage.setItem(ACTIVE_BOARD_STORAGE_KEY, uuid);
    else localStorage.removeItem(ACTIVE_BOARD_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable in private mode or quota-exceeded.
    // Silently ignore - the next hydrate cycle will re-pick from boards.
  }
}

export const useActiveBoardStore = defineStore('activeBoard', () => {
  const activeBoard = ref(null);
  const enabledSubjects = ref([]);
  const catalogBoards = ref([]);
  const isReady = ref(false);

  const availableBoards = computed(() => {
    const map = new Map();
    for (const subject of enabledSubjects.value) {
      const b = subject?.board;
      if (!b?.uuid) continue;
      if (!map.has(b.uuid)) {
        map.set(b.uuid, {
          uuid: b.uuid,
          name: b.name,
          display_order: b.display_order ?? 999,
        });
      }
    }
    return [...map.values()].sort(
      (a, b) =>
        a.display_order - b.display_order || a.name.localeCompare(b.name)
    );
  });

  const activeBoardName = computed(() => {
    const found = availableBoards.value.find(
      (b) => b.uuid === activeBoard.value
    );
    return found?.name ?? '';
  });

  const allBoards = computed(() =>
    [...catalogBoards.value].sort(
      (a, b) =>
        (a.display_order ?? 999) - (b.display_order ?? 999) ||
        a.name.localeCompare(b.name)
    )
  );

  function setEnabledSubjects(subjects) {
    enabledSubjects.value = Array.isArray(subjects) ? subjects : [];
  }

  function setCatalogBoards(boards) {
    catalogBoards.value = Array.isArray(boards) ? boards : [];
  }

  function setActiveBoard(uuid) {
    if (!uuid) return false;
    const found = availableBoards.value.find((b) => b.uuid === uuid);
    if (!found) return false;
    activeBoard.value = uuid;
    writeStorage(uuid);
    return true;
  }

  function hydrate() {
    const boards = availableBoards.value;
    if (!boards.length) {
      activeBoard.value = null;
      writeStorage(null);
      isReady.value = true;
      return;
    }
    const stored = readStorage();
    if (stored && boards.find((b) => b.uuid === stored)) {
      activeBoard.value = stored;
      writeStorage(stored);
    } else {
      activeBoard.value = boards[0].uuid;
      writeStorage(boards[0].uuid);
    }
    isReady.value = true;
  }

  /**
   * Fetches the student's enabled subjects via the injected `getFn`
   * (typically the api module's `get`), populates `enabledSubjects`,
   * and runs `hydrate`. Caller owns the import of the api module so
   * this store stays free of Vite-alias-only deps and remains testable
   * in plain node.
   */
  async function fetchAndHydrate(getFn) {
    try {
      const resp = await getFn('students/api/subjects/');
      enabledSubjects.value = resp?.data ?? [];
    } catch {
      enabledSubjects.value = [];
    }
    hydrate();
  }

  async function fetchBoardCatalog(getFn) {
    try {
      const resp = await getFn('api/boards/');
      setCatalogBoards(resp?.data ?? []);
    } catch {
      setCatalogBoards([]);
    }
  }

  function reset() {
    activeBoard.value = null;
    enabledSubjects.value = [];
    catalogBoards.value = [];
    isReady.value = false;
  }

  return {
    activeBoard,
    activeBoardName,
    availableBoards,
    allBoards,
    catalogBoards,
    enabledSubjects,
    isReady,
    setEnabledSubjects,
    setCatalogBoards,
    setActiveBoard,
    hydrate,
    fetchAndHydrate,
    fetchBoardCatalog,
    reset,
  };
});
